import {
  COMBINE_SHIPMENTS,
  CREATE_FRESH_SHIPMENT_EXPORT_SEAL_FORM_REPORT,
  CREATE_FRESH_SHIPMENT_FORM_REPORT,
  CREATE_FRESH_SHIPMENT_IMPLANT_FORM_REPORT,
  FertilizationQCResponse,
  GET_ESTIMATION_HEADER_VAL,
  GET_FRESH_SHIPMENT_LIST,
  GET_FRESH_SHIPMENT_QC,
  GET_SHIP_GROUP_LIST,
  SEND_COMM_DAIRY_FERTILIZATION_EMAIL,
  ShipGroupListResponse,
  TOA_GetFreshShipments,
  UNDO_SHIPMENT,
  UPDATE_FRESH_SHIPMENT_TRANSACTION,
  UPDATE_INCUBATOR_ASSIGNMENT,
  UPDATE_MATING_LABELS,
  UPDATE_MATING_LABELS_DUPES,
  UPDATE_QC_FRESH_SHIPMENT,
  UPDATE_SHIPMENT_GROUP_TRANSACTION,
} from '@/rtk-query.gql';

import { enhancedApi } from '../api/baseApi';

export type TempEmbryoOutcomeUploadRequest = {
  shipGroupIds: string[];
  fertilizationDate: string;
  fertilizationSiteId: string;
  rows: Record<string, unknown>[];
};

export type TempEmbryoOutcomeUploadResponse = {
  matched: number;
  unmatched: number;
  skippedVerified: number;
  errors: string[];
};

export const freshShipmentsApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getFreshShipmentList: build.query({
      query: (variables) => ({
        document: GET_FRESH_SHIPMENT_LIST,
        variables,
      }),
      providesTags: ['FreshShipmentList'],
      transformResponse: (response: {
        freshShipmentList: TOA_GetFreshShipments[];
      }) => response.freshShipmentList,
    }),
    getShipGroupList: build.query({
      query: (variables) => ({
        document: GET_SHIP_GROUP_LIST,
        variables,
      }),
      providesTags: ['ShipGroupList'],
      transformResponse: (response: { shipGroupList: ShipGroupListResponse }) =>
        response.shipGroupList,
    }),
    getFreshShipmentQC: build.query({
      query: (variables) => ({
        document: GET_FRESH_SHIPMENT_QC,
        variables,
      }),
      transformResponse: (response: {
        freshShipmentQC: FertilizationQCResponse;
      }) => response.freshShipmentQC,
      providesTags: ['QcFreshShipment'],
    }),
    getEstimationHeaderVal: build.query({
      query: (variables) => ({
        document: GET_ESTIMATION_HEADER_VAL,
        variables,
      }),
      providesTags: ['EstimationHeaderVal'],
    }),

    updateFreshShipmentTransaction: build.mutation({
      query: (variables) => ({
        document: UPDATE_FRESH_SHIPMENT_TRANSACTION,
        variables,
      }),
    }),
    updateQcFreshShipment: build.mutation({
      query: (variables) => ({
        document: UPDATE_QC_FRESH_SHIPMENT,
        variables,
      }),
      invalidatesTags: [
        'FreshShipmentList',
        'FertilizationListFreshShipment',
        'FertilizationQcData',
      ],
    }),

    updateMatingLabels: build.mutation({
      query: (variables) => ({
        document: UPDATE_MATING_LABELS,
        variables,
      }),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    updateMatingLabelsDupes: build.mutation({
      query: (variables) => ({
        document: UPDATE_MATING_LABELS_DUPES,
        variables,
      }),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    updateIncubatorAssignment: build.mutation({
      query: (variables) => ({
        document: UPDATE_INCUBATOR_ASSIGNMENT,
        variables,
      }),
    }),
    undoShipment: build.mutation({
      query: (variables) => ({
        document: UNDO_SHIPMENT,
        variables,
      }),
      invalidatesTags: ['FreshShipmentList'],
    }),
    combineShipments: build.mutation({
      query: (variables) => ({
        document: COMBINE_SHIPMENTS,
        variables,
      }),
      invalidatesTags: ['FreshShipmentList'],
    }),
    createFreshShipmentImplantFormReport: build.mutation({
      query: (variables) => ({
        document: CREATE_FRESH_SHIPMENT_IMPLANT_FORM_REPORT,
        variables,
      }),
    }),
    createFreshShipmentFormReport: build.mutation({
      query: (variables) => ({
        document: CREATE_FRESH_SHIPMENT_FORM_REPORT,
        variables,
      }),
    }),
    createFreshShipmentExportSealFormReport: build.mutation({
      query: (variables) => ({
        document: CREATE_FRESH_SHIPMENT_EXPORT_SEAL_FORM_REPORT,
        variables,
      }),
    }),
    sendCommDairyFertilizationEmail: build.mutation({
      query: (variables) => ({
        document: SEND_COMM_DAIRY_FERTILIZATION_EMAIL,
        variables,
      }),
    }),
    updateShipmentGroupTransaction: build.mutation({
      query: (variables) => ({
        document: UPDATE_SHIPMENT_GROUP_TRANSACTION,
        variables,
      }),
    }),
  }),
});

export const {
  useGetFreshShipmentListQuery,
  useGetShipGroupListQuery,
  useGetFreshShipmentQCQuery,
  useUpdateFreshShipmentTransactionMutation,
  useUpdateQcFreshShipmentMutation,
  useUndoShipmentMutation,
  useSendCommDairyFertilizationEmailMutation,
  useCombineShipmentsMutation,
  useCreateFreshShipmentImplantFormReportMutation,
  useCreateFreshShipmentFormReportMutation,
  useCreateFreshShipmentExportSealFormReportMutation,
  useUpdateShipmentGroupTransactionMutation,
  useLazyGetFreshShipmentListQuery,
  useLazyGetShipGroupListQuery,
  useLazyGetFreshShipmentQCQuery,
  useGetEstimationHeaderValQuery,
} = freshShipmentsApi;
